import { Link } from "react-router-dom";
import Reveal from "@/components/layout/Reveal";
import { PROJECTS, SERVICES } from "@/lib/site";
import { ArrowUpRight } from "lucide-react";

/**
 * PrestationInternalLinks — Section « Pour aller plus loin » on prestation pages.
 *
 * Renders descriptive-anchor internal links inside natural-language paragraphs
 * (never "En savoir plus" or "Découvrir"), plus 2-3 links to matching
 * réalisations, a link to the /prestations index, /entreprise and /contact.
 *
 * Props:
 *  - currentSlug (string) — the current prestation slug so we can filter it out
 *  - relatedCategories (string[]) — realisation categories to surface
 *  - bridgeText (JSX) — the transition paragraph specific to the current
 *                       prestation (contains the natural-language anchors).
 */
export default function PrestationInternalLinks({
    currentSlug,
    relatedCategories = [],
    bridgeText,
}) {
    // Pick up to 3 realisations matching the requested categories
    const realisations = PROJECTS.filter((p) =>
        relatedCategories.length
            ? relatedCategories.includes(p.category)
            : true
    ).slice(0, 3);

    // Other services (excluding the current one)
    const others = SERVICES.filter((s) => s.slug !== currentSlug);

    return (
        <section
            className="bg-white border-t border-concrete-200"
            data-testid="prestation-internal-links"
        >
            <div className="rcps-container py-16 md:py-20">
                <div className="grid md:grid-cols-12 gap-8 md:gap-10">
                    {/* Left column — natural language linking */}
                    <div className="md:col-span-7">
                        <Reveal>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Pour aller plus loin
                            </p>
                            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold text-navy-700 tracking-tight">
                                Explorez nos autres prestations et réalisations.
                            </h2>
                        </Reveal>

                        <Reveal delay={0.05}>
                            <div className="mt-6 space-y-4 text-navy-700/85 leading-relaxed">
                                {bridgeText}

                                <p>
                                    Retrouvez l’ensemble de nos services sur la page{" "}
                                    <Link
                                        to="/prestations"
                                        className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                                    >
                                        vue d’ensemble des prestations RCPS Terrassement
                                    </Link>
                                    , ou apprenez-en davantage sur{" "}
                                    <Link
                                        to="/entreprise"
                                        className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                                    >
                                        l’entreprise RCPS Terrassement à Plan-les-Ouates
                                    </Link>{" "}
                                    et son approche des chantiers à Genève et en Suisse
                                    romande.
                                </p>

                                <p>
                                    Vous avez un projet précis ?{" "}
                                    <Link
                                        to="/contact"
                                        className="text-navy-700 font-semibold underline underline-offset-2 hover:no-underline"
                                    >
                                        Demandez un devis pour votre chantier
                                    </Link>{" "}
                                    — nous étudions votre demande et revenons vers vous
                                    avec une proposition adaptée.
                                </p>
                            </div>
                        </Reveal>
                    </div>

                    {/* Right column — related realisations */}
                    <div className="md:col-span-5">
                        <Reveal delay={0.1}>
                            <p className="kicker text-navy-700">
                                <span className="hairline" /> Réalisations liées
                            </p>
                            <h3 className="mt-3 font-heading text-xl md:text-2xl font-semibold text-navy-700 tracking-tight">
                                Exemples de chantiers similaires
                            </h3>
                            <ul className="mt-5 space-y-3">
                                {realisations.map((p) => (
                                    <li key={p.id}>
                                        <Link
                                            to="/realisations"
                                            className="group block rounded-md border border-concrete-200 p-4 hover:border-navy-700 transition"
                                            data-testid={`related-realisation-${p.id}`}
                                        >
                                            <p className="text-[11px] uppercase tracking-[0.16em] text-navy-700/60">
                                                {p.category} · {p.location}
                                            </p>
                                            <p className="mt-1 font-heading font-semibold text-navy-700 leading-snug">
                                                {p.title}
                                            </p>
                                            <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-700">
                                                Consulter cette réalisation
                                                <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition" />
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>

                {/* Also visible list of other services with descriptive anchors */}
                <Reveal delay={0.15}>
                    <div className="mt-12 pt-8 border-t border-concrete-200">
                        <p className="kicker text-navy-700">
                            <span className="hairline" /> Nos autres prestations
                        </p>
                        <ul className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {others.map((s) => (
                                <li key={s.slug}>
                                    <Link
                                        to={`/prestations/${s.slug}`}
                                        className="group flex items-start gap-3 rounded-md border border-concrete-200 p-4 hover:border-navy-700 transition"
                                        data-testid={`related-service-${s.slug}`}
                                    >
                                        <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-navy-700 flex-shrink-0" />
                                        <span className="font-heading font-semibold text-navy-700 leading-snug">
                                            {s.title} à Genève et en Suisse romande
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
